'use client';

import { type ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * Refetch state for Agent Insights widgets. Each widget keeps its card chrome +
 * header and swaps only the body.
 *
 * - `RefreshBar`: filter/date change while stale data stays visible, a thin
 *   brand sweep under the card header; pair with `refreshDimClass` on the body.
 * - `CalculatingState`: shape-agnostic first-load placeholder (indeterminate
 *   sweep + pixel field + copy). Prefer a shape-matched skeleton from
 *   `widgetSkeletons.tsx` for Agent Insights widgets; this stays for surfaces
 *   outside that set (home admin sections, code review, model router) whose
 *   bodies have no strong silhouette to preview.
 *
 * First-load states for Agent Insights widgets live in `widgetSkeletons.tsx`:
 * shape-matched skeletons that mirror each widget's real layout (chart axes,
 * table rows, stat tiles).
 */

/**
 * PostHog/nprogress-style progress bar shown while a widget refetches after a
 * filter or date change: fills quickly, then creeps and holds near 90% until
 * the fresh data lands and the bar unmounts. Sits flush under the card header;
 * the stale body stays visible behind `refreshDimClass`.
 */
export function RefreshBar({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'relative h-1 w-full overflow-hidden bg-ds-gray-subtlest',
        className
      )}
      role="progressbar"
      aria-label="Updating"
    >
      <span className="ftu-refresh-fill absolute inset-y-0 left-0 w-full rounded-r-full bg-ds-brand-subtle" />
    </div>
  );
}

/** Body treatment while stale data is shown during a refetch. */
export const refreshDimClass =
  'pointer-events-none opacity-40 transition-opacity duration-200';

interface CalculatingStateProps {
  /** Personalized progress heading, e.g. "Calculating adoption health…" */
  title: string;
  /** What is being processed, e.g. "We're processing AI activity to generate team-level engagement insights." */
  description: string;
  /** Layout-only overrides (e.g. min-height to match the widget body). */
  className?: string;
  /**
   * Body illustration override. Defaults to the `.pixel-shader` block matrix,
   * which is shape-agnostic on purpose. Pass a shape-matched skeleton when the
   * widget's real content has a strong silhouette worth previewing (an area
   * chart, a donut, a heatmap), painted with the shared `widget-skeleton-*`
   * classes so it moves like every other loader.
   */
  illustration?: ReactNode;
}

export function CalculatingState({
  title,
  description,
  className,
  illustration,
}: CalculatingStateProps) {
  return (
    <div
      className={cn(
        // h-full/flex-1: card bodies are usually taller than the placeholder's
        // own min-height, and without these the block sits pinned to the top of
        // the card instead of centering in it.
        'relative flex h-full min-h-[180px] w-full flex-1 flex-col overflow-hidden',
        className
      )}
      role="status"
      aria-busy
      aria-label={title}
    >
      {/* Indeterminate sweep — "still computing", no real progress to report */}
      <div
        className="absolute inset-x-0 top-0 h-[3px] overflow-hidden bg-ds-gray-subtlest"
        aria-hidden
      >
        <span className="calculating-bar-sweep absolute inset-y-0 left-0 w-2/5 rounded-full bg-ds-brand-subtle" />
      </div>

      <div className="flex flex-1 flex-col items-center justify-center gap-5 p-6 text-center">
        {/* Pixel matrix with a band of light sweeping through it — "computing",
            without a bar silhouette implying the result is a bar chart. */}
        {illustration ?? (
          <div
            className="pixel-shader pixel-shader-fade widget-skeleton-breathe relative h-14 w-full max-w-[240px]"
            aria-hidden
          />
        )}

        <div className="flex w-full flex-col items-center gap-2 text-center">
          <p className="ds-text-body-sm-semibold text-ds-text-default">
            {title}
          </p>
          <p className="ds-text-body-xs-medium w-full max-w-[300px] text-ds-text-subtle">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
}
